<template>
    <widget-card :count="alerts.length || 0" :icon="QrCode" :loading="loading" :title="trans('dashboard.v2.alert_monitor.title')">
        <div class="d-flex align-items-start h-100 gap-4 overflow-x-auto">
            <template v-if="loading">
                <AlertMonitorWidgetItem :data="{}" :loading="true" />
            </template>
            <template v-else-if="alerts.length === 0">
                <empty-widget-state :text="trans('dashboard.v2.alert_monitor.empty_state')" />
            </template>
            <template v-else>
                <AlertMonitorWidgetItem
                    v-for="alert in alerts"
                    :key="alert.id"
                    :data="alert"
                    :loading="loading"
                    :processing="processingItems.includes(alert.id)"
                    @delete="handleDelete"
                    @move-to-progress="handleMoveToProgress"
                    @move-to-done="handleMoveToDone"
                    @move-to-to-do="handleMoveToToDo"
                    @show-task-detail="showTaskDetail"
                />
            </template>
        </div>
    </widget-card>
    <confirm-work-order-deletion-modal />
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { $http } from '@/bootstrap';
import { DateTime } from 'luxon';
import QrCode from '../../../../../icons/QrCode.vue';
import WidgetCard from '../../Components/WidgetCard.vue';
import AlertMonitorWidgetItem from './AlertMonitorWidgetItem.vue';
import { trans } from '@/mixins/i18n';
import { eventBus, events } from '@/eventBus';
import ConfirmWorkOrderDeletionModal from '@/Pages/WorkOrders/Modals/ConfirmWorkOrderDeletionModal.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import { loadWorkOrderDetails } from '@/Pages/WorkOrders/loadWorkOrderDetails';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
    },
    filters: Object,
});

const loading = ref(true);
const alerts = ref([]);
const processingItems = ref([]);

const formatInoperableSince = (isoString) => {
    const inoperableSince = DateTime.fromISO(isoString);
    const now = DateTime.now();
    const diff = now.diff(inoperableSince, ['hours', 'minutes']);
    const hoursAgo = Math.floor(diff.hours);
    const minutesAgo = Math.round(diff.minutes);

    let dateString;
    if (inoperableSince.hasSame(now, 'day')) {
        dateString = trans('dashboard.v2.alert_monitor.today');
    } else if (inoperableSince.hasSame(now.minus({ days: 1 }), 'day')) {
        dateString = trans('dashboard.v2.alert_monitor.yesterday');
    } else {
        dateString = inoperableSince.toFormat('MMM d');
    }

    const timePart = inoperableSince.toFormat('h:mm a');

    if (hoursAgo > 0) {
        return trans('dashboard.v2.alert_monitor.inoperable_since_format_hours', {
            hours: hoursAgo,
            time: timePart,
            date: dateString,
        });
    }

    return trans('dashboard.v2.alert_monitor.inoperable_since_format_minutes', {
        minutes: minutesAgo,
        time: timePart,
        date: dateString,
    });
};

const fetchData = async () => {
    try {
        loading.value = true;
        const response = await $http.get(props.widgetData.dataUrl);
        alerts.value = response.data.map((alert) => ({
            ...alert,
            inoperableSince: formatInoperableSince(alert.createdAt),
        }));
    } catch (error) {
        console.error('Failed to fetch alert monitor data:', error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchData();
});

const updateWorkOrderStatus = async (alert, status) => {
    try {
        loading.value = true;
        const response = await $http.put(route('workorders.status.update', alert.id), {
            status,
        });

        if (response.data.message) {
            eventBus.$emit(events.presentFlashMessage, {
                payload: {
                    message: response.data.message,
                    status: 'success',
                },
            });
        }

        await fetchData();
    } catch (error) {
        console.error(`Failed to move work order to ${status.replace('_', ' ')}:`, error);
        eventBus.$emit(events.presentFlashMessage, {
            payload: {
                message: error.response?.data?.message || 'Failed to update work order status',
                status: 'error',
            },
        });
    } finally {
        loading.value = false;
    }
};

const handleMoveToToDo = (alert) => updateWorkOrderStatus(alert, 'to_do');

const handleMoveToProgress = (alert) => updateWorkOrderStatus(alert, 'in_progress');

const handleMoveToDone = (alert) => updateWorkOrderStatus(alert, 'done');

const handleDelete = (alert) => {
    eventBus.$emit(events.openDeleteWorkOrderModal, {
        workOrder: alert,
        onDeleting: () => {
            loading.value = true;
        },
        onDeleted: () => {
            loading.value = false;
            fetchData();
        },
    });
};

const showTaskDetail = (workorderId) => {
    processingItems.value.push(workorderId);

    loadWorkOrderDetails(workorderId, {
        listeners: {
            saved: () => {
                fetchData();
            },
        },
    }).finally(() => {
        processingItems.value = processingItems.value.filter((id) => id !== workorderId);
    });
};
</script>
